/**
 * TickerSymbol - Styled ticker display with Matrix green glow
 * Variants: hero (large), inline (small), card (medium)
 */
import { YStack, XStack, Text } from 'tamagui';

export type TickerSymbolVariant = 'hero' | 'inline' | 'card';

interface TickerSymbolProps {
  symbol: string;
  companyName?: string;
  exchange?: string;
  change?: string;
  variant?: TickerSymbolVariant;
}

export function TickerSymbol({
  symbol,
  companyName,
  exchange,
  change,
  variant = 'card'
}: TickerSymbolProps) {
  // Variant sizes
  const sizes = {
    hero: {
      symbolSize: '$7',  // 48px
      changeSize: '$6',  // 32px
      metaSize: '$2',    // 13px
      padding: '$6',
      borderWidth: 4,
    },
    card: {
      symbolSize: '$5',  // 24px
      changeSize: '$4',  // 18px
      metaSize: '$1',    // 11px
      padding: '$4',
      borderWidth: 2,
    },
    inline: {
      symbolSize: '$3',  // 15px
      changeSize: '$2',  // 13px
      metaSize: '$1',    // 11px
      padding: '$2',
      borderWidth: 1,
    },
  };

  const s = sizes[variant];

  // Determine change color using pastel finance indicators
  const changeColor = change?.startsWith('+')
    ? '#7dd3c0'  // Pastel teal-green for positive
    : change?.startsWith('-')
    ? '#f4a6b8'  // Gentle rose for negative (not harsh red!)
    : '#9ba5b0'; // Medium gray-blue for neutral

  return (
    <YStack
      padding={s.padding}
      backgroundColor="$backgroundHover"
      borderLeftWidth={s.borderWidth}
      borderLeftColor="$green10"
      borderRadius="$2"
      minWidth={variant === 'inline' ? 120 : 200}
      $sm={{
        minWidth: variant === 'inline' ? 100 : 160,
      }}
    >
      <XStack alignItems="baseline" gap="$3" flexWrap="wrap">
        <Text
          fontFamily="$heading"
          fontSize={s.symbolSize}
          fontWeight="bold"
          color="$green10"
        >
          {symbol}
        </Text>
        {change && (
          <Text
            fontFamily="$mono"
            fontSize={s.changeSize}
            color={changeColor}
            fontWeight="600"
          >
            {change}
          </Text>
        )}
      </XStack>

      {(companyName || exchange) && (
        <Text
          fontSize={s.metaSize}
          color="$placeholderColor"
          fontFamily="$mono"
          marginTop="$1"
        >
          {companyName}{exchange ? ` • ${exchange}` : ''}
        </Text>
      )}
    </YStack>
  );
}
